import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource, MatDialog, MatSnackBar, MatPaginator } from '@angular/material';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { ComponentPageTitle } from 'src/app/page-title/page-title.component';
import { AsignarCitaLejanaDialogComponent } from './asignar-cita-lejana-dialog/asignar-cita-lejana-dialog.component';

/**
 * Pantalla de auto-asignacion de citas lejanas (>5km).
 * Lista los registros de auto_assign_cita_lejana con filtro por estado.
 * Si el usuario actual tiene uno ya tomado (atendido=1), solo le mostramos
 * ese hasta que termine de asignar/atender.
 */
@Component({
  selector: 'app-autoasignacion-lejanas',
  templateUrl: './autoasignacion-lejanas.component.html',
  styleUrls: ['./autoasignacion-lejanas.component.scss']
})
export class AutoasignacionLejanasComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;

  private destroy$ = new Subject<void>();
  private refreshTimer: any = null;
  private audio = new Audio('assets/audio/alerta.wav');
  private pendientesPrev = new Set<number>();
  private primeraCarga = true;

  columns: string[] = ['ID', 'TECNICO', 'PROVINCIA', 'DISTANCIA', 'FECHA', 'ESTADO', 'TOMADO_POR', 'ACCION'];
  dataSource = new MatTableDataSource<any>([]);
  resultsLength = 0;

  filtroEstado: string = '';  // '' todos, '0' pendiente, '1' tomado, '2' atendido
  filtroFecha: Date = new Date();  // default: hoy
  filtroProvincia: any = '';  // '' = todas, o idprovincia
  provincias: any[] = [];     // catalogo provincias para el filtro
  propiaTomada: boolean = false;  // si el backend dice que el usuario tiene una tomada
  cargando: boolean = false;

  constructor(
    private router: Router,
    private spinner: NgxSpinnerService,
    private gateway: GatewayService,
    public pageTitle: ComponentPageTitle,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private localSave: LocalSaveService,
  ) { }

  ngOnInit() {
    this.pageTitle.title = 'Alerta asignación lejana';
    this.audio.volume = 0.7;
    // Cargar catalogo de provincias para el filtro
    this.cargarProvincias();
    // Auto-refresh cada minuto
    this.refreshTimer = setInterval(() => this.cargar(true), 60000);
  }

  cargarProvincias() {
    this.gateway.get('getprovincias', {}).then((res: any) => {
      this.provincias = Array.isArray(res.rows) ? res.rows : [];
    }).catch(() => { this.provincias = []; });
  }

  ngAfterViewInit() {
    // Bindear paginator antes de la primera carga para que calcule length
    this.dataSource.paginator = this.paginator;
    this.cargar();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.refreshTimer) { clearInterval(this.refreshTimer); }
  }

  cargar(silencioso: boolean = false) {
    if (this.cargando) { return; }
    this.cargando = true;
    if (!silencioso) { this.spinner.show(); }
    const user = this.localSave.get('usuarioPGV') || {};
    const idusuario = user.IDU || user.idagente || null;
    const payload: any = { idusuario };
    if (this.filtroEstado !== '') {
      payload.atendido = Number(this.filtroEstado);
    }
    if (this.filtroFecha) {
      payload.fecha = this.formatFecha(this.filtroFecha);
    }
    if (this.filtroProvincia !== '' && this.filtroProvincia !== null && this.filtroProvincia !== undefined) {
      payload.idprovincia = this.filtroProvincia;
    }
    this.gateway.get('autoAsignLejListar', payload).then((res: any) => {
      const rows = Array.isArray(res.rows) ? res.rows : [];
      this.dataSource.data = rows;
      // Re-asegurar el binding del paginator cada vez (el paginator sigue siendo el mismo)
      if (this.paginator && this.dataSource.paginator !== this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
      this.resultsLength = rows.length;
      this.propiaTomada = !!res.propiaTomada;

      // Detectar pendientes NUEVOS vs los que ya teniamos cargados.
      // Solo toca alarma del 2da carga en adelante (evita sonar al abrir la pantalla).
      const pendientesActuales = new Set<number>(
        rows.filter(r => Number(r.atendido) === 0).map(r => Number(r.id))
      );
      if (!this.primeraCarga) {
        let hayNuevo = false;
        pendientesActuales.forEach(id => {
          if (!this.pendientesPrev.has(id)) { hayNuevo = true; }
        });
        if (hayNuevo) { this.reproducirAlarma(); }
      }
      this.pendientesPrev = pendientesActuales;
      this.primeraCarga = false;

      this.cargando = false;
      if (!silencioso) { this.spinner.hide(); }
    }).catch(() => {
      this.cargando = false;
      if (!silencioso) { this.spinner.hide(); }
    });
  }

  private reproducirAlarma() {
    try {
      this.audio.currentTime = 0;
      const p = this.audio.play();
      if (p && typeof p.catch === 'function') { p.catch(() => { /* ignorado */ }); }
    } catch (e) { /* ignorado */ }
  }

  refrescarManual() {
    this.cargar();
  }

  cambiarFiltro() {
    // Al cambiar filtro reiniciamos baseline de pendientes para no disparar alarma erronea
    this.primeraCarga = true;
    this.pendientesPrev.clear();
    this.cargar();
  }

  private formatFecha(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  tomar(element: any) {
    const user = this.localSave.get('usuarioPGV') || {};
    const payload = {
      id: element.id,
      idusuario: user.IDU || user.idagente,
      nombre_usuario: this.localSave.get('userLoggedIn') || null,
      correo_usuario: this.localSave.get('email') || null
    };
    this.gateway.get('autoAsignLejTomar', payload).then((res: any) => {
      const r = Number(res.response);
      if (r === 1) {
        this.snackbar.open(res.message || 'Registro tomado', 'Cerrar', { duration: 2500 });
        this.abrirDialog({ ...element, idusuario: payload.idusuario });
      } else if (r === 3) {
        // Tecnico ya tiene cita → alarma marcada como No valida
        this.snackbar.open(res.message || 'Alarma no válida (técnico ya tiene cita).', 'Cerrar', { duration: 5000 });
      } else {
        this.snackbar.open(res.message || 'No se pudo tomar el registro', 'Cerrar', { duration: 5000 });
      }
      this.cargar();
    }).catch(() => {
      this.snackbar.open('Error al tomar', 'Cerrar', { duration: 3000 });
    });
  }

  abrirDialog(element: any) {
    const dialogRef = this.dialog.open(AsignarCitaLejanaDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      height: '85vh',
      disableClose: true,
      data: {
        id_registro: element.id,
        id_tecnico: element.id_tecnico,
        nombre_tecnico: element.nombre_tecnico
      }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cargar();
    });
  }

  /** Para registros ya tomados por mí: al click en acción abrir directo el dialog */
  continuarAsignacion(element: any) {
    this.abrirDialog(element);
  }

  /**
   * Retorna true si el registro fue tomado por el usuario logueado.
   * Solo ese usuario puede continuar la asignacion.
   */
  esMia(element: any): boolean {
    const user = this.localSave.get('usuarioPGV') || {};
    const miId = Number(user.IDU || user.idagente);
    return Number(element.idusuario) === miId;
  }

  estadoLabel(atendido: number): string {
    if (atendido === 1) { return 'Tomado'; }
    if (atendido === 2) { return 'Atendido'; }
    if (atendido === 3) { return 'No válida'; }
    if (atendido === 4) { return 'Anulada'; }
    if (atendido === 5) { return 'Esperar cita cercana'; }
    return 'Pendiente';
  }

  estadoColor(atendido: number): string {
    if (atendido === 1) { return '#f39c12'; }   // naranja  = tomado
    if (atendido === 2) { return '#2ecc71'; }   // verde    = atendido
    if (atendido === 3) { return '#95a5a6'; }   // gris     = no valida
    if (atendido === 4) { return '#7f8c8d'; }   // gris osc = anulada
    if (atendido === 5) { return '#3498db'; }   // azul     = esperar cita cercana
    return '#e74c3c';                           // rojo     = pendiente
  }
}
